import { nameByPlatform } from "~/constants/platfroms";
import { type IMarket, MarketStatus } from "~/types/event";
import type { Platform } from "~/types/platform";
import { formatDate } from "~/utils/formatDate";
import { getEndsInDisplay, toValidDate } from "~/utils/formatDateCounter";
import { formatCents } from "~/utils/formatPrice";
import { formatRowMoney } from "~/utils/formatRowMoney";
import type {
  IRowChangeDisplay,
  RowChangeTone,
} from "~/utils/getRowChangeDisplay";
import { plural } from "~/utils/plural";
import {
  type IMarketSettlement,
  MarketSettlementKind,
} from "~/utils/resolution/getMarketSettlement";
import {
  AWAITING_RESOLUTION_LABEL,
  CLOSED_LABEL,
  RESOLVED_LABEL,
  RESULT_NOT_AVAILABLE_LABEL,
  RESULT_WORD,
  UMA_DISPUTED_LABEL,
} from "~/utils/resolution/resolutionLabels";

import type { IPanelOutcome, IPanelPlatformRow } from "./buildPanelOutcomes";
import { marketsOfKind } from "./getOutcomeSettlement";
import type { ISpreadRange } from "./marketQualityTypes";
import { isSpreadRange, toTenthsOfCent } from "./spreadBand";

const WITHHELD = "—";

export const platformCount = (count: number): string =>
  plural(count, "platform", "platforms");

export const namesOf = (platforms: readonly Platform[]): string =>
  platforms.map((platform) => nameByPlatform[platform]).join(", ");

export const getPanelCountsLabel = (
  outcomes: number,
  markets: number,
  platforms: number,
  resolved = 0,
  disputed = 0
): string =>
  [
    `${plural(outcomes, "outcome", "outcomes")} · ${plural(markets, "market", "markets")} across ${platformCount(platforms)}`,
    resolved > 0 ? `${resolved} resolved` : "",
    disputed > 0 ? `${disputed} disputed` : "",
  ]
    .filter(Boolean)
    .join(" · ");

export const openMarkets = ({
  markets,
  settlement,
}: IPanelOutcome): IMarket[] =>
  marketsOfKind(markets, settlement.markets, MarketSettlementKind.OPEN);

// The markets the outcome's liquidity sums: the open ones while any trades,
// else the disputed ones (a settled market has no book left).
const liquidityMarkets = (outcome: IPanelOutcome): IMarket[] => {
  const open = openMarkets(outcome);

  return open.length
    ? open
    : marketsOfKind(
        outcome.markets,
        outcome.settlement.markets,
        MarketSettlementKind.DISPUTED
      );
};

export const getHiddenPlatformsNote = (
  platforms: readonly Platform[]
): string | null =>
  platforms.length
    ? `${namesOf(platforms)} hidden by your platform filter. PH Consensus and best prices still include ${platforms.length === 1 ? "it" : "them"}.`
    : null;

export const getPriceLinkTooltip = (
  side: "buy" | "sell",
  price: number,
  platform: Platform
): string =>
  `${side === "buy" ? "Buy" : "Sell"} Yes at ${formatCents(price)} on ${nameByPlatform[platform]} (opens in a new tab)`;

export const getOutcomeLiquidityTooltip = (outcome: IPanelOutcome): string => {
  const { liquidity, liquidityMissingPlatforms } = outcome;
  const markets = liquidityMarkets(outcome);

  if (!liquidityMissingPlatforms.length) {
    return `Liquidity across ${platformCount(markets.length)}.`;
  }

  const reporting = markets.filter((market) => market.liquidity != null).length;
  const across =
    (liquidity ?? 0) > 0
      ? `Liquidity across ${reporting} of ${platformCount(markets.length)}. `
      : "";
  const missing =
    liquidityMissingPlatforms.length === 1
      ? `${namesOf(liquidityMissingPlatforms)} reports no liquidity for this outcome, so it isn't included.`
      : `${namesOf(liquidityMissingPlatforms)} report no liquidity for this outcome, so they aren't included.`;

  return across + missing;
};

export const getPlatformLiquidityTooltip = (
  platform: Platform,
  liquidity: number | null
): string =>
  liquidity === null
    ? `${nameByPlatform[platform]} reports no liquidity for this market, so we can't show it here. Prices and volume are live.`
    : `Liquidity on ${nameByPlatform[platform]}.`;

interface ISettleDisplay {
  text: string;
  isUrgent: boolean;
}

export const getSettledStatusText = ({
  market,
  settlement: { result, resolvedAt },
}: IPanelPlatformRow): string => {
  if (market.unifiedStatus === MarketStatus.CLOSED) {
    return CLOSED_LABEL;
  }

  const resolved = resolvedAt
    ? `${RESOLVED_LABEL} ${formatDate(resolvedAt, { dateOnly: true })}`
    : RESOLVED_LABEL;
  const paid = result
    ? `${RESULT_WORD[result]} paid out 100¢`
    : RESULT_NOT_AVAILABLE_LABEL;

  return `${resolved} · ${paid}`;
};

// The settled row's subline tail (mock): "Outcome: Yes".
const getSettledSubline = (
  market: IMarket,
  { result }: IMarketSettlement
): string => {
  if (market.unifiedStatus === MarketStatus.CLOSED) {
    return CLOSED_LABEL;
  }

  return result ? `Outcome: ${RESULT_WORD[result]}` : RESOLVED_LABEL;
};

export const getSettleDisplay = (
  { market, settlement }: IPanelPlatformRow,
  now: Date
): ISettleDisplay | null => {
  if (settlement.kind === MarketSettlementKind.SETTLED) {
    return { text: getSettledSubline(market, settlement), isUrgent: false };
  }

  if (settlement.kind === MarketSettlementKind.DISPUTED) {
    return { text: UMA_DISPUTED_LABEL, isUrgent: false };
  }

  const end = toValidDate(market.endDate);

  if (!end) {
    return null;
  }

  const { formattedTime, isError, isPast } = getEndsInDisplay(end, now);

  return isPast
    ? { text: AWAITING_RESOLUTION_LABEL, isUrgent: false }
    : { text: `settles in ${formattedTime.trim()}`, isUrgent: isError };
};

export const getSinglePlatformSubline = ({
  platform,
  wording,
}: IPanelPlatformRow): string => `${nameByPlatform[platform]}: ${wording}`;

export const getChanceChangeChip = (
  fraction: number | null | undefined,
  minPoints: number
): IRowChangeDisplay | null => {
  if (fraction === null || fraction === undefined) {
    return null;
  }

  const points = Math.round(fraction * 100);

  if (Math.abs(points) < minPoints) {
    return null;
  }

  return points > 0
    ? { label: `+${points}%`, tone: "up" }
    : { label: `−${Math.abs(points)}%`, tone: "down" };
};

interface ISpreadQuality {
  label: "Tight" | "Moderate" | "Wide";
  tone: RowChangeTone;
}

export const getSpreadQuality = (
  spread: number | null
): ISpreadQuality | null => {
  if (spread === null) {
    return null;
  }

  const cents = spread * 100;

  if (cents < 1.5) {
    return { label: "Tight", tone: "up" };
  }

  if (cents < 3) {
    return { label: "Moderate", tone: "none" };
  }

  return { label: "Wide", tone: "down" };
};

// A market nobody has traded shows "—", never "$0" (PH-1345 §2).
export const formatPanelVolume = (volume: number): string =>
  volume > 0 ? formatRowMoney(volume) : WITHHELD;

export const formatConsensus = (consensus: number | null): string =>
  consensus === null ? WITHHELD : `${Math.round(consensus * 100)}%`;

export const formatSpread = (spread: number | null): string =>
  formatCents(spread, { forceFixed1: true });

const RANGE_SEPARATOR = "–";

export const formatSpreadRange = (
  range: ISpreadRange | null,
  separator: string = RANGE_SEPARATOR
): string => {
  if (!range) {
    return WITHHELD;
  }

  const high = formatCents(range.max, { fixedDecimals: 1 });

  return isSpreadRange(range)
    ? `${toTenthsOfCent(range.min).toFixed(1)}${separator}${high}`
    : high;
};
